import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip
} from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import ErrorIcon from '@mui/icons-material/Error';
import FlagIcon from '@mui/icons-material/Flag';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import moment from 'moment';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import MenteeProfileModal from '../home/MentorDash/Components/MenteeProfileModal';
import { humanize } from './OnboardingFieldsDrawer';
import MemberProgramsModal, { IMemberAdminProgram, IMemberProgram } from './MemberProgramsModal';
import MemberRiskAreasModal from './MemberRiskAreasModal';
import MemberDevelopmentTierModal from './MemberDevelopmentTierModal';
import MemberConversationsModal from './MemberConversationsModal';
import MemberPanelDialog from './MemberPanelDialog';
import { memberCapability } from './memberCapability';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import MyCapabilityTab from '../home/member-learning-lifecycle/MyCapabilityTab';
import { useCapabilityMember, useCapabilityMembers, useCapabilityOrg } from '../../../../queries/useCapability';
import type { ActivitySectionView, MemberSummaryView } from '../../../../api/capability';
import { LEVEL_COLOR, LEVEL_NAME } from '../Program/components/learning-lifecycle/CapabilityGapsList';
import { capabilityColor } from '../Program/components/learning-lifecycle/teamCapability';
import MetricButton from './MetricButton';
import { ProgramSummary } from './ProgramsView';
import OnboardingFieldsDrawer from './OnboardingFieldsDrawer';
import InviteMemberDialog from './InviteMemberDialog';
import PendingProfilesView from './PendingProfilesView';
import NudgeMemberDialog from './NudgeMemberDialog';
import DosenDropdown from '../../components/DosenDropdown';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { useAppSelector } from '../../../../redux/hooks';
import { getUserDetails } from '../../../../utils/orgName';
import useOrgLevelAdmin from '../../../../hooks/useOrgLevelAdmin';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { getLLAuthHeaders } from '../../../../utils/llAuthHeaders';
import { MEDALS, MedalKey } from '../../../../components/gamification/scorecardData';
import useCapabilityRelease from '../../../../hooks/useCapabilityRelease';
import { useMemberTypeFormatter } from '../../../../utils/memberTypeLabels';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { memberTier, programProgress, scopedPrograms, sortMembers, type SortDir, type SortKey } from './memberSort';
import { averageOfRequired, scoreOrDash } from '../../../../utils/requirementsScore';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';
import {
  MOBILE_CARD_SX,
  MobileActionButton,
  MobileAttributePills,
  MobileAttributesButton,
  MobileStat,
  MobileStats,
  mobileValueSx
} from './mobileMemberParts';

// Shape returned by GET /getOrgMembersProgramProgress/{orgId}
export interface IOrgMember {
  id: string;
  fullName: string;
  profilePicture: string;
  /**
   * Resolved attribute labels. A list the member answered with "none of these
   * apply to me" arrives as "<List>: None" (BE side), so an admin can tell a
   * deliberate decline from a list the member was never asked about — the
   * latter contributes nothing and leaves the cell blank.
   */
  memberAttributes: string[];
  memberType: string; // derived role (e.g. "mentee", "mentor, program_admin", "organization_admin")
  userType: string; // legacy onboarding category (Employee/Student) — dropped under orgLevelAdmin
  onboardingStatus?: string; // org-level lifecycle status (e.g. "Onboarded", "Engaged", "Adopted")
  lastLogin: number | null; // unix seconds, or null
  lastLoginIso: string | null;
  isMentee: boolean;
  missingFields?: string[]; // org-mandatory onboarding fields the member hasn't filled
  hasPendingInfo?: boolean; // true when missingFields is non-empty
  elliottConversations?: number; // lessons completed + Elliott chats (left-nav + workspace) — BE-computed
  riskFlags?: number; // UNRESOLVED lessons (ever flagged, not yet green) — BE-computed; resolving decrements it
  /**
   * The member's own Development Tier — the BE medals payload, same shape as
   * /gamification/learner/summary → medals. Null when they aren't a learner in
   * any program; absent from a BE that predates the Tier column.
   */
  developmentTier?: { overall: MedalKey; sections: unknown[] } | null;
  programs: IMemberProgram[];
  /** The programs they are an admin of and not a learner in; absent from a BE that predates Program Progress's Role. */
  adminPrograms?: IMemberAdminProgram[];
  /** Capability release (GET /v3/admin/capability/members): their score, strongest and weakest capability. */
  capability?: MemberSummaryView['capability'];
  /** Capability release: their Roleplays and Practice Rounds Activity sections. */
  capabilityActivity?: ActivitySectionView[];
  /** Capability release: the questions they have asked Elliott (each answered question once). */
  elliottQuestions?: number;
}

// Pending invite (invited_users row grouped by email) — people invited to the
// org who haven't signed up yet. They have no users row, so they can't appear
// in the members list; the Invited tab is their only surface.
export interface IInvitedMember {
  id: string;
  fullName: string;
  email: string;
  roles: string[];
  invitedAt: number | null; // unix seconds
  invitedAtIso: string | null;
  invitedBy: string;
  invitationURL: string; // Auth0 single-use ticket — doubles as a manual invite link
  programs: Array<{ id: string; name: string }>; // empty → org-only invite
}

const MAX_VISIBLE_ATTRIBUTES = 2;

// A phone: the program filter's chosen value and its options wrap rather than being cut.
const PHONE_DROPDOWN_WRAP = {
  [MOBILE_SX]: {
    '& .MuiSelect-select.MuiSelect-select, & .MuiSelect-select .MuiTypography-root': {
      whiteSpace: 'normal',
      overflow: 'visible',
      overflowWrap: 'anywhere'
    }
  }
};
const PHONE_MENU_WRAP = {
  [MOBILE_SX]: { width: 'auto', maxWidth: 'calc(100vw - 32px)', '& .MuiMenuItem-root': { whiteSpace: 'normal' } }
};

// The table with the Capability release: Name, Attributes (one, then +X), Last
// Login, Activity, Capability, Strongest and Weakest Capability, Completion,
// Elliott Conversations.
// The view is a table: a hairline under every row and down the left of every
// column but the first.
const GRID_LINE = '1px solid #262626';
const gridCell = (flex: number, minWidth: number, first = false) => ({
  flex,
  minWidth,
  boxSizing: 'border-box' as const,
  alignSelf: 'stretch',
  // Every column is centred but the name, which reads from the left.
  justifyContent: first ? 'flex-start' : 'center',
  textAlign: first ? ('left' as const) : ('center' as const),
  display: 'flex',
  alignItems: 'center',
  px: '12px',
  py: '12px',
  ...(first ? null : { borderLeft: GRID_LINE })
});
const RELEASE_COL = {
  name: gridCell(1.5, 190, true),
  attributes: gridCell(1.3, 150),
  lastLogin: gridCell(1, 110),
  activity: gridCell(1, 120),
  capability: gridCell(0.9, 100),
  strongest: gridCell(1.6, 170),
  weakest: gridCell(1.6, 170),
  completion: gridCell(0.9, 110),
  conversations: gridCell(1, 120)
};
// The table's rows and its header: the cells carry the spacing, the row the rule beneath.
const GRID_ROW = { gap: 0, px: '13px', py: 0, alignItems: 'stretch', borderBottom: GRID_LINE };

// A name is cut after this many characters (the whole of it is on hover).
const NAME_LIMIT = 20;
const cutName = (name: string) => (name.length > NAME_LIMIT ? `${name.slice(0, NAME_LIMIT).trimEnd()}...` : name);

// Flex weights so columns fill the screen width with even spacing; Attributes weighted wider.
// minWidth keeps columns readable (and triggers horizontal scroll) on narrow viewports.
const COL = {
  name: { flex: 1.5, minWidth: 120 },
  attributes: { flex: 1.7, minWidth: 170 },
  // Last Login onwards share an equal flex weight so they sit at even intervals.
  lastLogin: { flex: 1.1, minWidth: 90 },
  tier: { flex: 1.1, minWidth: 90 },
  riskAreas: { flex: 1.1, minWidth: 90 },
  completion: { flex: 1.1, minWidth: 80 },
  quiz: { flex: 1.1, minWidth: 80 },
  conversations: { flex: 1.1, minWidth: 90 }
};

const ROW_GAP = '12px';

const headerCellSx = {
  fontSize: 12,
  fontWeight: 600,
  color: '#868686',
  fontFamily: 'Inter, sans-serif',
  textTransform: 'uppercase' as const,
  lineHeight: 1.3,
  whiteSpace: 'normal' as const
};

// The arrow beside a sortable column header: first click ascending, then it
// flips. Unsorted columns show a neutral up/down so they read as clickable.
const SortArrow = ({ label, dir, onClick }: { label: string; dir: SortDir | null; onClick: () => void }) => {
  const Icon = dir === 'asc' ? ArrowUpwardIcon : dir === 'desc' ? ArrowDownwardIcon : UnfoldMoreIcon;
  return (
    <Tooltip title={dir === 'asc' ? 'Sort descending' : 'Sort ascending'} placement="top">
      <IconButton
        onClick={onClick}
        aria-label={`Sort by ${label}${dir ? ` (${dir === 'asc' ? 'ascending' : 'descending'})` : ''}`}
        sx={{
          width: 20,
          height: 20,
          p: 0,
          flexShrink: 0,
          color: dir ? '#F79420' : '#565656',
          '&:hover': { color: dir ? '#F79420' : '#B6B6B6' }
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );
};

// Learning Completion color: green ≥70, orange 40–69, red below.
const completionColor = (score: number): string => {
  if (score >= 70) return '#00972D';
  if (score >= 40) return '#F79420';
  return '#FF6A55';
};

// Quiz Score color: green ≥90, orange 70–89, red below.
const quizColor = (score: number): string => {
  if (score >= 90) return '#00972D';
  if (score >= 70) return '#F79420';
  return '#FF6A55';
};

// Header label with a hover tooltip explaining the column's definition.
const HeaderLabel = ({ label, tip }: { label: string; tip: string }) => (
  <Tooltip title={tip} placement="top">
    <Typography sx={{ ...headerCellSx, cursor: 'default', textAlign: 'center' }}>{label}</Typography>
  </Tooltip>
);

// Overall tier chip — the same outlined rectangle in the tier's colour that the
// learner's Development Tier panel uses.
const TierChip = ({
  tierKey,
  onClick,
  tip = 'View Learning Tier'
}: {
  tierKey: MedalKey;
  onClick: () => void;
  tip?: string;
}) => {
  const medal = MEDALS[tierKey];
  return (
    <Tooltip title={tip} placement="top">
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: '8px',
          py: '4px',
          m: 0,
          borderRadius: '4px',
          // `${hex}1F` / `${hex}66` = the tier colour at ~12% / ~40% alpha.
          bgcolor: `${medal.color}1F`,
          border: `1px solid ${medal.color}`,
          color: medal.color,
          cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.4px',
          lineHeight: 1,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          transition: 'box-shadow 0.15s',
          '&:hover': { boxShadow: `0 0 10px ${medal.color}66` }
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: medal.color }} />
        {medal.label}
      </Box>
    </Tooltip>
  );
};

const AttributePills = ({ attributes, max = MAX_VISIBLE_ATTRIBUTES }: { attributes: string[]; max?: number }) => {
  const visible = attributes.slice(0, max);
  const overflowAttrs = attributes.slice(max);
  const overflow = overflowAttrs.length;

  return (
    <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
      {visible.map((attr) => (
        <Box key={attr} sx={{ bgcolor: '#2A2A2A', borderRadius: '20px', px: '10px', py: '5px', flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: '#FDFDFD',
              fontFamily: 'Inter, sans-serif',
              whiteSpace: 'nowrap'
            }}
          >
            {attr}
          </Typography>
        </Box>
      ))}
      {overflow > 0 && (
        <Tooltip
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', py: '2px' }}>
              {overflowAttrs.map((attr) => (
                <Typography
                  key={attr}
                  sx={{ fontSize: 12, fontWeight: 500, color: '#FDFDFD', fontFamily: 'Inter, sans-serif' }}
                >
                  {attr}
                </Typography>
              ))}
            </Box>
          }
          placement="top"
        >
          <Box
            sx={{ bgcolor: '#2A2A2A', borderRadius: '20px', px: '8px', py: '3px', flexShrink: 0, cursor: 'default' }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: '#FDFDFD',
                fontFamily: 'Inter, sans-serif',
                whiteSpace: 'nowrap'
              }}
            >
              +{overflow}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

interface MemberRowProps {
  member: IOrgMember;
  onViewProfile: () => void;
  onViewProgress: () => void;
  onViewRiskAreas: () => void;
  onViewTier: () => void;
  profileLoading: boolean;
  /** Org-level admins allocate programs from the progress modal, so it opens even with no programs. */
  canManagePrograms: boolean;
  programFilter?: string;
  /** The Capability release's columns (and what they open). */
  release?: { onViewCapability: () => void; onViewConversations: () => void };
  /** A phone: the row as a card. Read once by the page rather than once per row. */
  isMobileApp?: boolean;
}

/**
 * A capability's name with the member's level on it beneath: the Strongest and
 * Weakest columns. Clicking it says where Elliott's analysis of it is.
 */
const CapabilityCell = ({
  pick,
  memberName,
  noRole = false,
  stacked = false
}: {
  pick: { name: string; level: keyof typeof LEVEL_NAME } | null;
  memberName: string;
  /** No role yet, so no capabilities: a dash, as on their own Capability tab. */
  noRole?: boolean;
  /** On a phone's member card: read from the left, and the whole name, wrapped. */
  stacked?: boolean;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (noRole) {
    return (
      <DosenTooltip title={`${memberName} has no role yet, so has no required capabilities.`}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: 'Inter, sans-serif', color: '#565656' }}>
          -
        </Typography>
      </DosenTooltip>
    );
  }
  if (!pick) {
    return (
      <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: 'Inter, sans-serif', color: '#565656' }}>
        Not assessed
      </Typography>
    );
  }
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={{
          display: 'block',
          maxWidth: '100%',
          minWidth: 0,
          p: 0,
          m: 0,
          border: 'none',
          bgcolor: 'transparent',
          textAlign: stacked ? 'left' : 'center',
          cursor: 'pointer',
          '&:hover .capability-name': { textDecoration: 'underline', textUnderlineOffset: '4px' }
        }}
      >
        <Typography
          className="capability-name"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'Inter, sans-serif',
            color: '#FDFDFD',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: stacked ? 'normal' : 'nowrap',
            ...(stacked ? { overflowWrap: 'anywhere' as const } : null)
          }}
        >
          {pick.name}
        </Typography>
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'Inter, sans-serif',
            color: LEVEL_COLOR[pick.level]
          }}
        >
          {LEVEL_NAME[pick.level]}
        </Typography>
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: '6px',
            maxWidth: 300,
            p: '10px 12px',
            // The house tooltip's card; the theme paints popovers black with !important, so these say it too.
            background: '#232323 !important',
            border: '1px solid #2A2A2A !important',
            borderRadius: '8px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55) !important'
          }
        }}
      >
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, lineHeight: '18px', color: '#FDFDFD' }}>
          Open {memberName}&rsquo;s Capability score to read Elliott&rsquo;s analysis of this capability.
        </Typography>
      </Popover>
    </>
  );
};

const MemberRow = ({
  member,
  onViewProfile,
  onViewProgress,
  onViewRiskAreas,
  onViewTier,
  profileLoading,
  canManagePrograms,
  programFilter,
  release,
  isMobileApp = false
}: MemberRowProps) => {
  const [hovered, setHovered] = useState(false);
  const capability = useMemo(() => (release ? memberCapability(member) : null), [release, member]);

  const textSx = {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Inter, sans-serif',
    color: hovered ? '#F79420' : '#FDFDFD',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  };
  const dashSx = { ...textSx, textAlign: 'center' as const, color: '#565656' };

  const lastLoginText = member.lastLogin ? moment.unix(member.lastLogin).fromNow() : 'Never';

  // Real metrics derived from the member's programs, scoped to the program
  // filter; no filter → average across all enrolled programs.
  const programs = scopedPrograms(member, programFilter);
  const learningCompletion = programProgress(member, programFilter) ?? 0;
  // Only the programs that assess them (DA-5056 R5): null, a dash, when none does.
  const avgQuizScore = averageOfRequired(programs.map((p) => p.mcqScore));
  const hasPrograms = programs.length > 0;

  // BE-computed Elliott metrics (getOrgMembersProgramProgress):
  // Elliott Conversations = lessons completed since launch + chats from left-nav Elliott + workspace (Teams) Elliott.
  // Risk Areas = unresolved lessons: ever flagged (scenario, quiz, or both) and not yet resolved via Improve Performance.
  // With the Capability release it counts questions only, the ones its modal lists (PRD §5.5).
  const elliottConversations = (release ? member.elliottQuestions : member.elliottConversations) ?? 0;
  const redFlags = member.riskFlags ?? 0;

  // Overall Learning Tier, as the member's own dashboard shows it (see memberTier).
  const tierKey = memberTier(member);
  const hasTier = Boolean(tierKey && MEDALS[tierKey]);

  const completionCell = hasPrograms ? (
    <MetricButton tip="View program progress" color={completionColor(learningCompletion)} onClick={onViewProgress}>
      {learningCompletion}%
    </MetricButton>
  ) : canManagePrograms ? (
    <MetricButton tip="Add to a program" color="#565656" onClick={onViewProgress}>
      None
    </MetricButton>
  ) : (
    <Typography sx={dashSx}>None</Typography>
  );

  // A phone: the row as a card, its columns as label/value pairs, the name opening the profile.
  if (isMobileApp) {
    const noRole = Boolean(capability && !capability.roleName);
    const muted = mobileValueSx('#565656');
    const hasPicks = Boolean(release && !noRole && (capability?.strongest || capability?.weakest));
    const tierFigure =
      hasTier && tierKey ? (
        <TierChip tierKey={tierKey} tip={release ? 'View activity' : 'View Learning Tier'} onClick={onViewTier} />
      ) : (
        <Typography sx={muted}>None</Typography>
      );
    return (
      <Box data-testid="member-row" data-member-id={member.id} sx={MOBILE_CARD_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            component="button"
            type="button"
            onClick={profileLoading ? undefined : onViewProfile}
            aria-label={`View ${member.fullName}'s profile`}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: 40,
              m: '-4px 0',
              p: 0,
              border: 'none',
              bgcolor: 'transparent',
              textAlign: 'left',
              cursor: 'pointer'
            }}
          >
            <Avatar
              src={member.profilePicture || undefined}
              alt=""
              sx={{ width: 36, height: 36, flexShrink: 0, bgcolor: '#2A2A2A', color: '#FDFDFD', fontSize: 14 }}
            >
              {member.fullName.trim().charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ ...mobileValueSx(), fontSize: 15 }}>{member.fullName}</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, color: '#868686', mt: '2px' }}>
                Last login {member.lastLogin ? lastLoginText : 'never'}
              </Typography>
            </Box>
            {profileLoading ? (
              <CircularProgress size={14} sx={{ color: '#868686', flexShrink: 0 }} />
            ) : (
              <PersonOutlineIcon sx={{ fontSize: 22, color: '#B6B6B6', flexShrink: 0 }} />
            )}
          </Box>
          {member.memberAttributes?.length > 0 && (
            <MobileAttributesButton memberName={member.fullName} attributes={member.memberAttributes} />
          )}
        </Box>
        {/* What the warning icon's tooltip says on desktop, said outright. */}
        {member.hasPendingInfo && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            <ErrorIcon sx={{ fontSize: 15, color: '#F79420', mt: '1px', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12.5, lineHeight: 1.4, color: '#F79420' }}>
              Incomplete profile
              {member.missingFields?.length ? `: missing ${member.missingFields.map(humanize).join(', ')}` : ''}
            </Typography>
          </Box>
        )}
        <MobileStats>
          {release ? (
            <>
              <MobileStat label="Activity">{tierFigure}</MobileStat>
              <MobileStat label="Capability">
                {noRole ? (
                  <Typography sx={muted}>No role yet</Typography>
                ) : typeof capability?.percent === 'number' ? (
                  <MetricButton
                    tip="View capability"
                    color={capabilityColor(capability.percent)}
                    onClick={release.onViewCapability}
                  >
                    {capability.percent}%
                  </MetricButton>
                ) : (
                  <Typography sx={muted}>Not assessed</Typography>
                )}
              </MobileStat>
              <MobileStat label="Completion">{completionCell}</MobileStat>
              {hasPicks && (
                <>
                  <MobileStat label="Strongest">
                    <CapabilityCell stacked pick={capability?.strongest ?? null} memberName={member.fullName} />
                  </MobileStat>
                  <MobileStat label="Weakest">
                    <CapabilityCell stacked pick={capability?.weakest ?? null} memberName={member.fullName} />
                  </MobileStat>
                </>
              )}
              <MobileStat label="Elliott conversations">
                {elliottConversations > 0 ? (
                  <MetricButton tip="View conversations" color="#FDFDFD" onClick={release.onViewConversations}>
                    {elliottConversations}
                  </MetricButton>
                ) : (
                  <Typography sx={muted}>0</Typography>
                )}
              </MobileStat>
            </>
          ) : (
            <>
              <MobileStat label="Learning tier">{tierFigure}</MobileStat>
              <MobileStat label="Learning gaps">
                {!hasPrograms ? (
                  <Typography sx={muted}>None</Typography>
                ) : redFlags > 0 ? (
                  <MetricButton tip="View red flags" color="#FF6A55" onClick={onViewRiskAreas}>
                    <FlagIcon sx={{ fontSize: 15, color: '#FF6A55' }} />
                    {redFlags}
                  </MetricButton>
                ) : (
                  <Typography sx={muted}>0</Typography>
                )}
              </MobileStat>
              <MobileStat label="Program progress">{completionCell}</MobileStat>
              <MobileStat label="Quiz score">
                <Typography
                  sx={mobileValueSx(hasPrograms && avgQuizScore !== null ? quizColor(avgQuizScore) : '#565656')}
                >
                  {hasPrograms ? scoreOrDash(avgQuizScore) : 'None'}
                </Typography>
              </MobileStat>
              <MobileStat label="Elliott conversations">
                <Typography sx={mobileValueSx()}>{elliottConversations}</Typography>
              </MobileStat>
            </>
          )}
        </MobileStats>
      </Box>
    );
  }

  return (
    <Box
      data-testid="member-row"
      data-member-id={member.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        gap: ROW_GAP,
        alignItems: 'center',
        px: '25px',
        py: '15px',
        bgcolor: hovered ? '#151515' : 'transparent',
        transition: 'background-color 0.1s',
        ...(release ? GRID_ROW : null)
      }}
    >
      {/* Name — click to open the member's profile */}
      <Box sx={{ ...(release ? RELEASE_COL.name : COL.name), display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Tooltip
          title={release && member.fullName.length > NAME_LIMIT ? `${member.fullName} · View profile` : 'View profile'}
        >
          <Typography
            onClick={profileLoading ? undefined : onViewProfile}
            sx={{
              ...textSx,
              cursor: profileLoading ? 'default' : 'pointer',
              '&:hover': { textDecoration: profileLoading ? 'none' : 'underline' }
            }}
          >
            {release ? cutName(member.fullName) : member.fullName}
          </Typography>
        </Tooltip>
        {profileLoading && <CircularProgress size={12} sx={{ color: '#868686', flexShrink: 0 }} />}
        {member.hasPendingInfo && (
          <Tooltip
            title={
              <Box>
                <Box sx={{ fontWeight: 600, mb: 0.5 }}>Incomplete profile</Box>
                <Box>Missing required: {(member.missingFields || []).map(humanize).join(', ')}</Box>
              </Box>
            }
          >
            <ErrorIcon
              sx={{ fontSize: 16, color: '#F79420', flexShrink: 0, alignSelf: 'flex-start', cursor: 'default' }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Attributes */}
      <Box sx={release ? RELEASE_COL.attributes : COL.attributes}>
        <AttributePills attributes={member.memberAttributes || []} max={release ? 1 : undefined} />
      </Box>

      {/* Last Login */}
      <Box sx={release ? RELEASE_COL.lastLogin : COL.lastLogin}>
        <Typography sx={textSx}>{lastLoginText}</Typography>
      </Box>

      {release ? (
        <>
          {/* Capability: the share of their capabilities at Capable or better, opening their Capability panel. */}
          <Box
            data-testid="member-capability"
            sx={{ ...RELEASE_COL.capability, display: 'flex', justifyContent: 'center' }}
          >
            {capability && !capability.roleName ? (
              // No role yet, so no capabilities to score.
              <DosenTooltip title={`${member.fullName} has no role yet, so has no required capabilities.`}>
                <Typography sx={dashSx}>-</Typography>
              </DosenTooltip>
            ) : typeof capability?.percent === 'number' ? (
              <MetricButton
                tip="View capability"
                color={capabilityColor(capability.percent)}
                onClick={release.onViewCapability}
              >
                {capability.percent}%
              </MetricButton>
            ) : (
              <Typography sx={dashSx}>Not assessed</Typography>
            )}
          </Box>
          <Box sx={RELEASE_COL.strongest}>
            <CapabilityCell
              pick={capability?.strongest ?? null}
              memberName={member.fullName}
              noRole={Boolean(capability && !capability.roleName)}
            />
          </Box>
          <Box sx={RELEASE_COL.weakest}>
            <CapabilityCell
              pick={capability?.weakest ?? null}
              memberName={member.fullName}
              noRole={Boolean(capability && !capability.roleName)}
            />
          </Box>
          {/* Activity: their tier, opening their Activity panel. */}
          <Box sx={{ ...RELEASE_COL.activity, display: 'flex', justifyContent: 'center' }}>
            {hasTier && tierKey ? (
              <TierChip tierKey={tierKey} tip="View activity" onClick={onViewTier} />
            ) : (
              <Typography sx={dashSx}>None</Typography>
            )}
          </Box>
          <Box sx={{ ...RELEASE_COL.completion, display: 'flex', justifyContent: 'center' }}>{completionCell}</Box>
          {/* Elliott Conversations: the count, opening their questions and Elliott's responses. */}
          <Box sx={{ ...RELEASE_COL.conversations, display: 'flex', justifyContent: 'center' }}>
            {elliottConversations > 0 ? (
              <MetricButton tip="View conversations" color="#FDFDFD" onClick={release.onViewConversations}>
                {elliottConversations}
              </MetricButton>
            ) : (
              <Typography sx={dashSx}>0</Typography>
            )}
          </Box>
        </>
      ) : (
        <>
          {/* Tier — the member's overall Development Tier; opens their tier panel.
          A member with no tier data gets an em dash, never a placeholder tier. */}
          <Box sx={{ ...COL.tier, display: 'flex', justifyContent: 'center' }}>
            {hasTier && tierKey ? (
              <TierChip tierKey={tierKey} onClick={onViewTier} />
            ) : (
              <Typography sx={dashSx}>None</Typography>
            )}
          </Box>

          {/* Risk Areas — unresolved red-flag lessons; opens the red flags behind the count */}
          <Box sx={{ ...COL.riskAreas, display: 'flex', justifyContent: 'center' }}>
            {!hasPrograms ? (
              <Typography sx={dashSx}>None</Typography>
            ) : redFlags > 0 ? (
              <MetricButton tip="View red flags" color="#FF6A55" onClick={onViewRiskAreas}>
                <FlagIcon sx={{ fontSize: 15, color: '#FF6A55' }} />
                {redFlags}
              </MetricButton>
            ) : (
              <Typography sx={dashSx}>0</Typography>
            )}
          </Box>

          {/* Program Progress — avg program progress across enrolled programs; opens the per-program breakdown */}
          <Box sx={{ ...COL.completion, display: 'flex', justifyContent: 'center' }}>{completionCell}</Box>

          {/* Quiz Score — avg quiz score across all enrolled programs */}
          <Box sx={COL.quiz}>
            <Typography
              sx={{
                ...textSx,
                textAlign: 'center',
                color: hasPrograms && avgQuizScore !== null ? quizColor(avgQuizScore) : '#565656'
              }}
            >
              {hasPrograms ? scoreOrDash(avgQuizScore) : 'None'}
            </Typography>
          </Box>

          {/* Elliott Conversations */}
          <Box sx={COL.conversations}>
            <Typography sx={{ ...textSx, textAlign: 'center', color: hovered ? '#F79420' : '#FDFDFD' }}>
              {elliottConversations}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

// Invited tab columns.
const INV_COL = {
  name: { flex: 1.4, minWidth: 140 },
  email: { flex: 1.8, minWidth: 200 },
  roles: { flex: 1, minWidth: 100 },
  programs: { flex: 1.6, minWidth: 160 },
  invited: { flex: 1, minWidth: 100 },
  invitedBy: { flex: 1.6, minWidth: 160 },
  action: { flex: 1, minWidth: 120 }
};

const InvitedRow = ({ invite, isMobileApp = false }: { invite: IInvitedMember; isMobileApp?: boolean }) => {
  const [hovered, setHovered] = useState(false);
  // Invite role codes ("mentee", "mentor", "admin") read as the org's own words.
  const formatRoles = useMemberTypeFormatter();

  const textSx = {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Inter, sans-serif',
    color: hovered ? '#F79420' : '#FDFDFD',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  };
  const mutedSx = { ...textSx, fontWeight: 400, color: hovered ? '#F79420' : '#C9CDD3' };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(invite.invitationURL);
      toast.success('Invite link copied');
    } catch {
      toast.error('The invite link could not be copied. Try again.');
    }
  };

  // A phone: the invite as a card, its columns as label/value pairs.
  if (isMobileApp) {
    return (
      <Box sx={MOBILE_CARD_SX}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...mobileValueSx(), fontSize: 15 }}>{invite.fullName}</Typography>
          <Typography sx={{ ...mobileValueSx('#C9CDD3'), fontSize: 13, fontWeight: 400, mt: '2px' }}>
            {invite.email}
          </Typography>
        </Box>
        <MobileStats>
          <MobileStat label="Role">
            <Typography sx={mobileValueSx()}>{formatRoles(invite.roles.join(', '), 'None')}</Typography>
          </MobileStat>
          <MobileStat label="Invited">
            <Typography sx={mobileValueSx()}>
              {invite.invitedAt ? moment.unix(invite.invitedAt).fromNow() : 'Not set'}
            </Typography>
          </MobileStat>
          <MobileStat label="Invited for">
            {invite.programs.length ? (
              <MobileAttributePills attributes={invite.programs.map((p) => p.name)} />
            ) : (
              <Typography sx={mobileValueSx('#C9CDD3')}>Organization</Typography>
            )}
          </MobileStat>
          <MobileStat label="Invited by">
            <Typography sx={{ ...mobileValueSx('#C9CDD3'), fontWeight: 400 }}>
              {invite.invitedBy || 'Not set'}
            </Typography>
          </MobileStat>
        </MobileStats>
        {/* The Auth0 ticket, for an invitee whose email never arrived. */}
        {invite.invitationURL && (
          <DosenButton
            tone="basic"
            sizeVariant="sm"
            onClick={handleCopyLink}
            sx={{ alignSelf: 'stretch', minHeight: 40 }}
          >
            Copy Invite Link
          </DosenButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        gap: ROW_GAP,
        alignItems: 'center',
        px: '25px',
        py: '15px',
        bgcolor: hovered ? '#151515' : 'transparent',
        transition: 'background-color 0.1s'
      }}
    >
      <Box sx={INV_COL.name}>
        <Typography sx={textSx}>{invite.fullName}</Typography>
      </Box>
      <Box sx={INV_COL.email}>
        <Typography sx={mutedSx}>{invite.email}</Typography>
      </Box>
      <Box sx={INV_COL.roles}>
        <Typography sx={textSx}>{formatRoles(invite.roles.join(', '), 'None')}</Typography>
      </Box>
      {/* Invited For: target programs, or the organization itself (org-only invite) */}
      <Box sx={INV_COL.programs}>
        {invite.programs.length ? (
          <AttributePills attributes={invite.programs.map((p) => p.name)} />
        ) : (
          <Typography sx={mutedSx}>Organization</Typography>
        )}
      </Box>
      <Box sx={INV_COL.invited}>
        <Typography sx={textSx}>{invite.invitedAt ? moment.unix(invite.invitedAt).fromNow() : 'Not set'}</Typography>
      </Box>
      <Box sx={INV_COL.invitedBy}>
        <Typography sx={mutedSx}>{invite.invitedBy || 'Not set'}</Typography>
      </Box>
      {/* The invitation URL is the Auth0 single-use ticket — handing it over
          manually unblocks invitees whose email never arrived. */}
      <Box sx={{ ...INV_COL.action, display: 'flex', alignItems: 'center' }}>
        {invite.invitationURL ? (
          <DosenButton tone={hovered ? 'cta' : 'basic'} sizeVariant="sm" onClick={handleCopyLink}>
            Copy Link
          </DosenButton>
        ) : (
          <Typography sx={mutedSx}>None</Typography>
        )}
      </Box>
    </Box>
  );
};

/** One member's Capability panel: their levels on every capability of their role, and their trend. */
const MemberCapabilityPanel: React.FC<{ member: { id: string; name: string } }> = ({ member }) => {
  const { data, isLoading } = useCapabilityMember(member.id);
  if (isLoading) {
    return (
      <Typography
        role="status"
        sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#868686', textAlign: 'center', pt: '8px' }}
      >
        Loading {member.name}&apos;s capability…
      </Typography>
    );
  }
  const capability = data && data.measures.length > 0 ? { roleName: data.roleName, measures: data.measures } : null;
  return <MyCapabilityTab capability={capability} trend={data?.trend} member={member} />;
};

const OrgMembersPage = () => {
  // The Learning Tier column's rule changes with the Capability release (utils/capabilityRelease.ts).
  const capabilityRelease = useCapabilityRelease();
  // Each member's Capability figures, Roleplays and Practice Rounds, and questions to Elliott.
  const { data: capabilityMembers } = useCapabilityMembers(capabilityRelease);
  const orgLevelAdmin = useOrgLevelAdmin();
  const { location, isPlatformAdmin } = useProgramContext();
  const { orgId: urlOrgId } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  // The endpoint requires the Auth0 org ID (org_XXXXXXXX). orgDetails may be
  // unhydrated on direct navigation, so fall back to the JWT claim.
  const reduxAuthOrgId = useAppSelector((state) => state.orgDetails.data?.authId);
  const tokenAuthOrgId = getUserDetails().orgId;
  const effectiveOrgId = reduxAuthOrgId || tokenAuthOrgId || urlOrgId;

  const [members, setMembers] = useState<IOrgMember[]>([]);
  const [invitedMembers, setInvitedMembers] = useState<IInvitedMember[]>([]);
  const [view, setView] = useState<'members' | 'invited'>('members');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // One detail modal per clickable figure: Program Progress, Risk Areas, Tier.
  const [progressMember, setProgressMember] = useState<IOrgMember | null>(null);
  const [riskMember, setRiskMember] = useState<IOrgMember | null>(null);
  const [tierMember, setTierMember] = useState<IOrgMember | null>(null);
  // The Capability release adds their Capability panel and their Elliott Conversations.
  const [capabilityMember, setCapabilityMember] = useState<IOrgMember | null>(null);
  const [conversationsMember, setConversationsMember] = useState<IOrgMember | null>(null);
  const [programFilter, setProgramFilter] = useState<string>(''); // '' = all
  const [pendingOnly, setPendingOnly] = useState(false); // show only members with pending required info
  const [programs, setPrograms] = useState<ProgramSummary[]>([]);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [profileLoadingId, setProfileLoadingId] = useState<string | null>(null);
  const [requiredFieldKeys, setRequiredFieldKeys] = useState<string[]>([]);
  const [nudgeMember, setNudgeMember] = useState<IOrgMember | null>(null);
  // The org a platform admin is looking at, for the calls that need it named (the nudge).
  const capabilityOrg = useCapabilityOrg();
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);
  const isMobileApp = useIsMobileApp();
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

  // A fresh column starts ascending; the same column flips.
  const toggleSort = (key: SortKey) =>
    setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const sortArrow = (key: SortKey, label: string) => (
    <SortArrow label={label} dir={sort?.key === key ? sort.dir : null} onClick={() => toggleSort(key)} />
  );

  // Export Data — the export endpoint is program-scoped (emails an XLSX report,
  // incl. the Feedback Questions & Answers column), so a program must be picked.
  const handleExportData = async () => {
    const targetProgramId = programFilter || (programs.length === 1 ? programs[0].id : '');
    if (!targetProgramId) {
      toast.info('Select a program in the filter to export its people data.');
      return;
    }
    setExporting(true);
    toast.success('Your report will be emailed to you shortly');
    try {
      await API.exportUsers({
        grpId: targetProgramId,
        location: location || '',
        orgId: effectiveOrgId || '',
        plan: false
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'The report could not be exported. Try again.');
    } finally {
      setExporting(false);
    }
  };

  // Fetch a member's full profile and open the shared profile modal. The endpoint
  // is group-scoped, so we use one of the member's programs (falling back to the
  // org id) as the group context.
  const handleViewProfile = async (member: IOrgMember) => {
    if (profileLoadingId) return;
    const grpId = member.programs?.[0]?.id || effectiveOrgId;
    if (!grpId) {
      toast.error('This profile could not be loaded. Try again.');
      return;
    }
    setProfileLoadingId(member.id);
    try {
      const res = await API.getUserProfileInAGroup(grpId, member.id);
      if (res?.status === 200) {
        setProfileUser(res?.data?.userDetails || res?.data?.data || res?.data || {});
      } else {
        toast.error('This profile could not be loaded. Try again.');
      }
    } catch {
      toast.error('This profile could not be loaded. Try again.');
    } finally {
      setProfileLoadingId(null);
    }
  };

  // Employee-view members, honouring the program filter (orgLevelAdmin only).
  useEffect(() => {
    if (!effectiveOrgId) return;
    let cancelled = false;
    const fetchMembers = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await API.getOrgMembers(
          effectiveOrgId,
          orgLevelAdmin && programFilter ? programFilter : undefined
        );
        if (!cancelled && response.status === 200) {
          const fetched: IOrgMember[] = response.data?.members || [];
          setMembers(fetched);
          setInvitedMembers(response.data?.invitedMembers || []);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMembers();
    return () => {
      cancelled = true;
    };
  }, [effectiveOrgId, programFilter, orgLevelAdmin, refreshKey]);

  // Program list — powers both the Employee-view filter and the Program view.
  // Only published-or-later programs (Q3: published by default). Non-fatal.
  useEffect(() => {
    if (!orgLevelAdmin) return;
    const fetchPrograms = async () => {
      const { idtoken, authorization } = getLLAuthHeaders();
      if (!idtoken || !authorization) return;
      try {
        const res = await API.getV3AdminPrograms({
          orgId: isPlatformAdmin ? effectiveOrgId : undefined,
          idtoken,
          authorization,
          location
        });
        const list = Array.isArray(res?.data?.programs) ? res.data.programs : [];
        const mapped: ProgramSummary[] = list
          .filter((p: any) => p.status !== 'not-started' && p.status !== 'design')
          .map((p: any) => ({
            id: String(p.id),
            name: p.name || 'Untitled',
            model: p.model || '',
            rolesOnly: Array.isArray(p.roleIds) && p.roleIds.length > 0,
            ownerId: p.owner?.id ? String(p.owner.id) : p.creator?.id ? String(p.creator.id) : null
          }));
        setPrograms(mapped);
        // Deep link from elsewhere (e.g. the admin dashboard's Programs Due →
        // "Review Progress") can preselect the filter. `programId` is exact;
        // `program` carries the display name, resolved here once the list is
        // known. An unmatched name simply leaves the filter on All Programs.
        const requestedId = searchParams.get('programId');
        const requestedName = searchParams.get('program');
        if (requestedId && mapped.some((p) => p.id === requestedId)) {
          setProgramFilter(requestedId);
        } else if (requestedName) {
          const match = mapped.find((p) => p.name.toLowerCase() === requestedName.toLowerCase());
          if (match) setProgramFilter(match.id);
        }
      } catch {
        /* non-fatal: filter + program view simply stay empty */
      }
    };
    fetchPrograms();
  }, [orgLevelAdmin, effectiveOrgId, isPlatformAdmin, location]);

  // Required onboarding fields — drives the dynamic per-field columns in the
  // Incomplete Profiles view (mirrors the required-field calc in OnboardingFieldsDrawer).
  useEffect(() => {
    if (!orgLevelAdmin || !effectiveOrgId) return;
    let cancelled = false;
    const fetchRequiredFields = async () => {
      try {
        const res = await API.getOrgOnboardingFields(effectiveOrgId);
        const data = res?.data || {};
        const avail: string[] = Array.isArray(data.availableFields) ? data.availableFields : [];
        const mand: string[] = Array.isArray(data.mandatoryFields) ? data.mandatoryFields : [];
        const configured: Array<{ key: string; required: boolean }> = Array.isArray(data.onboardingFields)
          ? data.onboardingFields
          : [];
        const mandSet = new Set(mand);
        const requiredByKey = new Map(configured.map((f) => [f.key, f.required]));
        const requiredKeys = avail.filter((k) => mandSet.has(k) || Boolean(requiredByKey.get(k)));
        if (!cancelled) setRequiredFieldKeys(requiredKeys);
      } catch {
        /* non-fatal: Incomplete Profiles view simply shows no field columns */
      }
    };
    fetchRequiredFields();
    return () => {
      cancelled = true;
    };
  }, [orgLevelAdmin, effectiveOrgId]);

  const withCapability = useMemo(() => {
    if (!capabilityRelease || !capabilityMembers) return members;
    const byId = new Map(capabilityMembers.map((c) => [c.id, c]));
    return members.map((m) => {
      const c = byId.get(m.id);
      return c
        ? {
            ...m,
            capability: c.capability,
            capabilityActivity: c.activitySections,
            elliottQuestions: c.elliottQuestions
          }
        : m;
    });
  }, [members, capabilityMembers, capabilityRelease]);

  // Name search runs across both tabs, so a name typed on Members still finds
  // someone who's only been invited.
  const query = searchTerm.trim().toLowerCase();
  const matchesSearch = (name: string) => !query || (name || '').toLowerCase().includes(query);

  const displayedMembers = sortMembers(
    withCapability.filter((m) => (!pendingOnly || m.hasPendingInfo) && matchesSearch(m.fullName)),
    sort,
    orgLevelAdmin ? programFilter : undefined
  );

  // The BE invite list isn't program-filtered; honour the toolbar filter here.
  const displayedInvited = invitedMembers.filter(
    (i) => (!programFilter || i.programs.some((p) => p.id === programFilter)) && matchesSearch(i.fullName)
  );
  const emptyMessage = (fallback: string) => (query ? `No one matches "${searchTerm.trim()}"` : fallback);

  const searchField = (
    <TextField
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="Search people"
      size="small"
      inputProps={{ 'aria-label': 'Search people by name' }}
      sx={{
        // Gives way before the toolbar's buttons do.
        width: 220,
        minWidth: 140,
        flexShrink: 1,
        '& .MuiOutlinedInput-root': {
          bgcolor: 'rgba(0, 0, 0, 0.70)',
          borderRadius: '6px',
          color: '#FDFDFD',
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          px: '12px',
          '& fieldset': { borderColor: '#444444' },
          '&:hover fieldset': { borderColor: '#565656' },
          '&.Mui-focused fieldset': { borderColor: '#F79420' }
        },
        '& .MuiOutlinedInput-input': { py: '8px', px: 0 },
        '& .MuiOutlinedInput-input::placeholder': { color: '#868686', opacity: 1 },
        // A phone: the row's width, beside the program filter, as tall as it.
        [MOBILE_SX]: { width: 'auto', flex: 1, minWidth: 0, '& .MuiOutlinedInput-input': { py: '10px' } }
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <SearchIcon sx={{ color: '#FDFDFD', fontSize: 18 }} />
          </InputAdornment>
        )
      }}
    />
  );

  const renderMemberRow = (member: IOrgMember) => (
    <MemberRow
      key={member.id}
      member={member}
      onViewProfile={() => handleViewProfile(member)}
      onViewProgress={() => setProgressMember(member)}
      onViewRiskAreas={() => setRiskMember(member)}
      onViewTier={() => setTierMember(member)}
      profileLoading={profileLoadingId === member.id}
      canManagePrograms={orgLevelAdmin}
      programFilter={orgLevelAdmin ? programFilter : undefined}
      isMobileApp={isMobileApp}
      release={
        capabilityRelease
          ? {
              onViewCapability: () => setCapabilityMember(member),
              onViewConversations: () => setConversationsMember(member)
            }
          : undefined
      }
    />
  );

  // A phone has no column heads to sort by: the same sorts sit behind one button that names the sort in use.
  const sortOptions: Array<{ key: SortKey; label: string }> = [
    { key: 'name', label: 'Name' },
    { key: 'lastLogin', label: 'Last login' },
    { key: 'tier', label: capabilityRelease ? 'Employee activity' : 'Learning tier' },
    { key: 'progress', label: capabilityRelease ? 'Completion' : 'Program progress' }
  ];
  const activeSort = sort ? sortOptions.find((o) => o.key === sort.key) : undefined;
  const sortControl = (
    <>
      <DosenButton
        tone="basic"
        sizeVariant="sm"
        aria-haspopup="menu"
        aria-label={
          activeSort && sort
            ? `Sorted by ${activeSort.label}, ${sort.dir === 'asc' ? 'ascending' : 'descending'}. Change the sort`
            : 'Sort people'
        }
        onClick={(e: React.MouseEvent<HTMLElement>) => setSortAnchor(e.currentTarget)}
        sx={{ flexShrink: 0, minHeight: 40, whiteSpace: 'nowrap' }}
      >
        {activeSort && sort ? `${activeSort.label} ${sort.dir === 'asc' ? '↑' : '↓'}` : 'Sort'}
      </DosenButton>
      <Menu
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: '6px',
            minWidth: 220,
            background: '#232323 !important',
            border: '1px solid #2A2A2A !important',
            borderRadius: '8px'
          }
        }}
      >
        {sortOptions.map((o) => {
          const on = sort?.key === o.key;
          const Arrow = sort?.dir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
          return (
            <MenuItem
              key={o.key}
              selected={on}
              onClick={() => {
                toggleSort(o.key);
                setSortAnchor(null);
              }}
              sx={{
                minHeight: 44,
                gap: '12px',
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                color: on ? '#F79420' : '#FDFDFD'
              }}
            >
              <Box component="span" sx={{ flex: 1 }}>
                {o.label}
              </Box>
              {on && <Arrow sx={{ fontSize: 16 }} />}
            </MenuItem>
          );
        })}
        {sort && (
          <MenuItem
            onClick={() => {
              setSort(null);
              setSortAnchor(null);
            }}
            sx={{ minHeight: 44, fontFamily: 'Inter, sans-serif', fontSize: 14, color: '#B6B6B6' }}
          >
            Default order
          </MenuItem>
        )}
      </Menu>
    </>
  );

  const tabSx = (active: boolean) => ({
    px: '4px',
    pb: '10px',
    cursor: 'pointer',
    borderBottom: active ? '2px solid #F79420' : '2px solid transparent',
    color: active ? '#FDFDFD' : '#868686',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Inter, sans-serif',
    '&:hover': { color: '#FDFDFD' }
  });

  return (
    <Box>
      {/* One toolbar row (orgLevelAdmin — invites are org-scoped): program
          filter, then the Members / Invited tabs, then the actions on the right. */}
      {orgLevelAdmin && !isMobileApp && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, borderBottom: '1px solid #2A2A2A', pb: 1 }}>
          <DosenDropdown
            options={[{ value: '', label: 'All Programs' }, ...programs.map((p) => ({ value: p.id, label: p.name }))]}
            value={programFilter}
            onChange={(v) => setProgramFilter(v as string)}
            placeholder="All Programs"
            hideIcon
            sx={{ width: 240 }}
            inputSx={{ py: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, alignSelf: 'stretch' }}>
            <Typography onClick={() => setView('members')} sx={tabSx(view === 'members')}>
              People
            </Typography>
            <Typography onClick={() => setView('invited')} sx={tabSx(view === 'invited')}>
              Invited{invitedMembers.length ? ` (${invitedMembers.length})` : ''}
            </Typography>
          </Box>
          {searchField}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DosenButton tone="cta" sizeVariant="sm" onClick={() => setInviteOpen(true)}>
              Invite People
            </DosenButton>
            {view === 'members' && (
              <DosenButton
                tone={pendingOnly ? 'cta' : 'basic'}
                sizeVariant="sm"
                onClick={() => setPendingOnly((v) => !v)}
                aria-pressed={pendingOnly}
              >
                Incomplete Profiles
              </DosenButton>
            )}
            <DosenButton tone="basic" sizeVariant="sm" onClick={() => setOnboardingOpen(true)}>
              Onboarding Fields
            </DosenButton>
            {view === 'members' && (
              <DosenButton tone="basic" sizeVariant="sm" onClick={handleExportData} disabled={exporting}>
                {exporting ? 'Exporting...' : 'Export Data'}
              </DosenButton>
            )}
          </Box>
        </Box>
      )}
      {/* A phone: the tabs on top, then the program filter and search, then the
          actions centred on one row. */}
      {orgLevelAdmin && isMobileApp && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mb: '12px' }}>
          <Box role="tablist" sx={{ display: 'flex', gap: '20px', borderBottom: '1px solid #2A2A2A' }}>
            <Typography
              role="tab"
              aria-selected={view === 'members'}
              onClick={() => setView('members')}
              sx={{ ...tabSx(view === 'members'), fontSize: 15, pt: '6px' }}
            >
              People
            </Typography>
            <Typography
              role="tab"
              aria-selected={view === 'invited'}
              onClick={() => setView('invited')}
              sx={{ ...tabSx(view === 'invited'), fontSize: 15, pt: '6px' }}
            >
              Invited{invitedMembers.length ? ` (${invitedMembers.length})` : ''}
            </Typography>
          </Box>
          <DosenDropdown
            options={[{ value: '', label: 'All Programs' }, ...programs.map((p) => ({ value: p.id, label: p.name }))]}
            value={programFilter}
            onChange={(v) => setProgramFilter(v as string)}
            placeholder="All Programs"
            hideIcon
            sx={{ width: '100%' }}
            inputSx={{ py: 1, ...PHONE_DROPDOWN_WRAP }}
            menuSx={PHONE_MENU_WRAP}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {searchField}
            {view === 'members' && !pendingOnly && sortControl}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <MobileActionButton
              primary
              name="Invite People"
              label="Invite"
              Icon={PersonAddAlt1OutlinedIcon}
              onClick={() => setInviteOpen(true)}
            />
            {view === 'members' && (
              <MobileActionButton
                name="Incomplete Profiles"
                label="Incomplete"
                Icon={ErrorOutlineIcon}
                active={pendingOnly}
                onClick={() => setPendingOnly((v) => !v)}
              />
            )}
            <MobileActionButton
              name="Onboarding Fields"
              label="Onboarding"
              Icon={ChecklistIcon}
              onClick={() => setOnboardingOpen(true)}
            />
            {view === 'members' && (
              <MobileActionButton
                name={exporting ? 'Exporting data' : 'Export Data'}
                label={exporting ? 'Exporting' : 'Export'}
                Icon={FileDownloadOutlinedIcon}
                disabled={exporting}
                onClick={handleExportData}
              />
            )}
          </Box>
        </Box>
      )}
      {/* No toolbar without orgLevelAdmin — the search sits on its own. */}
      {!orgLevelAdmin && (
        <Box sx={{ mb: 2, [MOBILE_SX]: { display: 'flex', gap: '8px', mb: '12px' } }}>
          {searchField}
          {isMobileApp && sortControl}
        </Box>
      )}

      <Box
        sx={{
          bgcolor: '#1C1C1C',
          border: '1px solid #2A2A2A',
          borderRadius: '8px',
          overflowY: 'auto',
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 150px)',
          // A phone: no table frame or inner scroll; the cards carry their own surface and the page scrolls.
          [MOBILE_SX]: {
            bgcolor: 'transparent',
            border: 'none',
            borderRadius: 0,
            overflow: 'visible',
            maxHeight: 'none'
          }
        }}
      >
        {loading ? (
          <Box aria-busy aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: '25px', py: '16px', borderBottom: '1px solid #2A2A2A' }}>
              <Bone width="60%" height={14} />
            </Box>
            {Array.from({ length: 8 }, (_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: ROW_GAP, px: '25px', py: '15px' }}>
                <Bone width="18%" height={16} />
                <Bone width="22%" height={22} radius="20px" />
                <Box sx={{ flex: 1 }} />
                <Bone width="40%" height={16} />
              </Box>
            ))}
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
              People could not be loaded. Refresh the page and try again.
            </Typography>
          </Box>
        ) : view === 'invited' && isMobileApp ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {displayedInvited.length === 0 ? (
              <Typography
                sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14, textAlign: 'center', py: 6 }}
              >
                {emptyMessage('No pending invites')}
              </Typography>
            ) : (
              displayedInvited.map((invite) => <InvitedRow key={invite.id} invite={invite} isMobileApp />)
            )}
          </Box>
        ) : view === 'invited' ? (
          <Box sx={{ minWidth: 1000 }}>
            {/* Invited header — mirrors the members table styling */}
            <Box
              sx={{
                display: 'flex',
                gap: ROW_GAP,
                alignItems: 'center',
                px: '25px',
                py: '16px',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: '#1C1C1C',
                borderBottom: '1px solid #2A2A2A'
              }}
            >
              <Box sx={INV_COL.name}>
                <Typography sx={headerCellSx}>Name</Typography>
              </Box>
              <Box sx={INV_COL.email}>
                <Typography sx={headerCellSx}>Email</Typography>
              </Box>
              <Box sx={INV_COL.roles}>
                <Typography sx={headerCellSx}>Role</Typography>
              </Box>
              <Box sx={INV_COL.programs}>
                <Typography sx={headerCellSx}>Invited for</Typography>
              </Box>
              <Box sx={INV_COL.invited}>
                <Typography sx={headerCellSx}>Invited</Typography>
              </Box>
              <Box sx={INV_COL.invitedBy}>
                <Typography sx={headerCellSx}>Invited by</Typography>
              </Box>
              <Box sx={INV_COL.action}>
                <HeaderLabel
                  label="Invite link"
                  tip="The personal invitation link from the invite email. Copy and share it directly with the invitee if the email never arrived."
                />
              </Box>
            </Box>

            {displayedInvited.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
                  {emptyMessage('No pending invites')}
                </Typography>
              </Box>
            ) : (
              displayedInvited.map((invite) => <InvitedRow key={invite.id} invite={invite} />)
            )}
          </Box>
        ) : pendingOnly ? (
          <PendingProfilesView
            members={displayedMembers}
            requiredFieldKeys={requiredFieldKeys}
            onNudge={setNudgeMember}
          />
        ) : isMobileApp ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', ...fadeInSx }}>
            {displayedMembers.length === 0 ? (
              <Typography
                sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14, textAlign: 'center', py: 6 }}
              >
                {emptyMessage('No people found')}
              </Typography>
            ) : (
              displayedMembers.map(renderMemberRow)
            )}
          </Box>
        ) : (
          // Inner wrapper sets a min width so the extra columns scroll rather than clip on narrow viewports
          <Box sx={{ minWidth: capabilityRelease ? 1380 : 1200, ...fadeInSx }}>
            {/* Table Header — sticky within this scroll container */}
            <Box
              sx={{
                display: 'flex',
                gap: ROW_GAP,
                alignItems: 'center',
                px: '25px',
                py: '16px',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: '#1C1C1C',
                borderBottom: '1px solid #2A2A2A',
                ...(capabilityRelease ? { ...GRID_ROW, borderBottom: '1px solid #2A2A2A' } : null)
              }}
            >
              {capabilityRelease ? (
                <>
                  <Box sx={{ ...RELEASE_COL.name, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={headerCellSx}>Name</Typography>
                    {sortArrow('name', 'name')}
                  </Box>
                  <Box sx={{ ...RELEASE_COL.attributes, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={headerCellSx}>Attributes</Typography>
                  </Box>
                  <Box sx={{ ...RELEASE_COL.lastLogin, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={headerCellSx}>Last login</Typography>
                    {sortArrow('lastLogin', 'last login')}
                  </Box>
                  <Box
                    sx={{
                      ...RELEASE_COL.capability,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    <HeaderLabel
                      label="Capability"
                      tip="The share of the member's assessed capabilities at Capable or better. Click a figure to see their Capability panel."
                    />
                  </Box>
                  <Box sx={{ ...RELEASE_COL.strongest, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <HeaderLabel
                      label="Strongest Capability"
                      tip="The capability the member holds the highest level on, with that level."
                    />
                  </Box>
                  <Box sx={{ ...RELEASE_COL.weakest, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <HeaderLabel
                      label="Weakest Capability"
                      tip="The capability the member holds the lowest level on, with that level."
                    />
                  </Box>
                  <Box
                    sx={{
                      ...RELEASE_COL.activity,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    <HeaderLabel
                      label="Employee Activity"
                      tip="The member's Activity level: the average of their Activity measures, each counting equally, as on their own dashboard. Click a level to see their Activity panel."
                    />
                    {sortArrow('tier', 'activity')}
                  </Box>
                  <Box
                    sx={{
                      ...RELEASE_COL.completion,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    <HeaderLabel
                      label="Completion"
                      tip="Average progress across all enrolled programs, or in the selected program. Click a figure to see progress in each program."
                    />
                    {sortArrow('progress', 'completion')}
                  </Box>
                  <Box
                    sx={{
                      ...RELEASE_COL.conversations,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    <HeaderLabel
                      label="Elliott Conversations"
                      tip="Conversations the member has had with Elliott. Click a count to read their questions and Elliott's responses."
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ ...COL.name, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={headerCellSx}>Name</Typography>
                    {sortArrow('name', 'name')}
                  </Box>
                  <Box sx={COL.attributes}>
                    <Typography sx={headerCellSx}>Attributes</Typography>
                  </Box>
                  <Box sx={{ ...COL.lastLogin, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={headerCellSx}>Last login</Typography>
                    {sortArrow('lastLogin', 'last login')}
                  </Box>
                  <Box
                    sx={{ ...COL.tier, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                  >
                    <HeaderLabel
                      label="Learning Tier"
                      tip={
                        capabilityRelease
                          ? "The member's overall Learning Tier: the average of their Activity measures, each counting equally, as on their own dashboard. Click a tier to see each measure and how they can improve it."
                          : "The member's overall Learning Tier: the lowest tier they hold across the five measures. Click a tier to see each measure and how they can improve it."
                      }
                    />
                    {sortArrow('tier', 'learning tier')}
                  </Box>
                  <Box sx={COL.riskAreas}>
                    <HeaderLabel
                      label="Learning Gaps"
                      tip="Number of lessons with an unresolved red flag — a failed Live Scenario or (where quiz retakes are enabled) an uncorrected quiz question. The count drops as the member resolves items through Improve Performance. Click a count to see the red flags behind it."
                    />
                  </Box>
                  <Box
                    sx={{
                      ...COL.completion,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    <HeaderLabel
                      label="Program progress"
                      tip={`${
                        orgLevelAdmin && programFilter
                          ? 'Progress in the selected program.'
                          : 'Average progress across all enrolled programs.'
                      } Click a figure to see progress in each program.`}
                    />
                    {sortArrow('progress', 'program progress')}
                  </Box>
                  <Box sx={COL.quiz}>
                    <HeaderLabel
                      label="Quiz score"
                      tip={
                        orgLevelAdmin && programFilter
                          ? 'Quiz score in the selected program.'
                          : 'Average quiz score across all enrolled programs.'
                      }
                    />
                  </Box>
                  <Box sx={COL.conversations}>
                    <HeaderLabel
                      label="Elliott Conversations"
                      tip="Conversations employees have had with Elliott either via Live Scenario Training in lessons or Elliott Conversations."
                    />
                  </Box>
                </>
              )}
            </Box>

            {/* Table Body */}
            {displayedMembers.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
                  {emptyMessage('No people found')}
                </Typography>
              </Box>
            ) : (
              displayedMembers.map(renderMemberRow)
            )}
          </Box>
        )}
      </Box>

      <MenteeProfileModal open={Boolean(profileUser)} onClose={() => setProfileUser(null)} user={profileUser} />

      {progressMember && (
        <MemberProgramsModal
          open={Boolean(progressMember)}
          onClose={() => setProgressMember(null)}
          memberName={progressMember.fullName}
          memberId={progressMember.id}
          programs={progressMember.programs}
          adminPrograms={progressMember.adminPrograms}
          {...(orgLevelAdmin
            ? {
                authId: effectiveOrgId || '',
                allPrograms: programs,
                memberIsOrgAdmin: /organization_admin/.test(progressMember.memberType || ''),
                onChanged: () => setRefreshKey((k) => k + 1)
              }
            : {})}
        />
      )}

      {riskMember && (
        <MemberRiskAreasModal
          open={Boolean(riskMember)}
          onClose={() => setRiskMember(null)}
          memberName={riskMember.fullName}
          memberId={riskMember.id}
          programs={riskMember.programs}
        />
      )}

      {tierMember?.developmentTier && (
        <MemberDevelopmentTierModal
          open={Boolean(tierMember)}
          onClose={() => setTierMember(null)}
          memberId={tierMember.id}
          memberName={tierMember.fullName}
          developmentTier={tierMember.developmentTier}
          programs={tierMember.programs}
          capabilityActivity={tierMember.capabilityActivity}
        />
      )}

      {capabilityMember && (
        <MemberPanelDialog
          open
          onClose={() => setCapabilityMember(null)}
          title={`${capabilityMember.fullName}'s Capability`}
        >
          <MemberCapabilityPanel member={{ id: capabilityMember.id, name: capabilityMember.fullName }} />
        </MemberPanelDialog>
      )}

      {conversationsMember && (
        <MemberConversationsModal
          open
          onClose={() => setConversationsMember(null)}
          memberId={conversationsMember.id}
          memberName={conversationsMember.fullName}
          count={
            (capabilityRelease ? conversationsMember.elliottQuestions : conversationsMember.elliottConversations) ?? 0
          }
        />
      )}

      {orgLevelAdmin && (
        <OnboardingFieldsDrawer
          open={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
          authId={effectiveOrgId || ''}
        />
      )}

      {orgLevelAdmin && inviteOpen && (
        <InviteMemberDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          authId={effectiveOrgId || ''}
          programs={programs}
          invitedEmails={invitedMembers.map((m) => m.email)}
          onInvited={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {nudgeMember && (
        <NudgeMemberDialog
          open={Boolean(nudgeMember)}
          memberId={nudgeMember.id}
          memberName={nudgeMember.fullName}
          orgId={capabilityOrg}
          onClose={() => setNudgeMember(null)}
        />
      )}
    </Box>
  );
};

export default OrgMembersPage;
